/**
 * 지출관리 BdExpense API 헬퍼 (isBdApiEnabled 시 /common/select|save)
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
  parseJsonObject,
  parseJsonArray,
} from 'services/commonQuery';

export function dimsKey(dims = {}) {
  return `${String(dims.tenantCode || '').trim()}|${String(dims.fiscalYear || '').trim()}`;
}

export function dimsParams(dims = {}) {
  return {
    tenantCode: String(dims.tenantCode || '').trim(),
    fiscalYear: Number(dims.fiscalYear) || dims.fiscalYear,
  };
}

function clearMod(rows) {
  return (rows || []).map((r) => ({ ...r, mod: '' }));
}

async function saveParentChildren({
  dims,
  cache,
  loadFn,
  parentUidKey,
  childUidKey,
  parentSaveQuery,
  childSaveQuery,
  nextParents,
  nextChildren,
  ensureParentUid,
  ensureChildUid,
  mapParentToApi,
  mapChildToApi,
}) {
  const key = dimsKey(dims);
  const params = dimsParams(dims);
  const prevBundle = cacheGet(cache, key) || (await loadFn(dims));
  const prevParents = prevBundle.masters || prevBundle.payments || [];
  const prevChildren = prevBundle.vendors || prevBundle.details || prevBundle.docs || prevBundle.contracts || [];

  const dirtyP = diffRowsByUid(
    prevParents,
    nextParents,
    parentUidKey,
    ensureParentUid
  ).map((r) => (r.mod === 'D' ? r : mapParentToApi ? mapParentToApi(r) : r));

  const deletedParentUids = new Set(dirtyP.filter((r) => r.mod === 'D').map((r) => r[parentUidKey]));
  const childrenKept = (nextChildren || []).filter((c) => !deletedParentUids.has(c[parentUidKey]));
  const dirtyC = [
    ...diffRowsByUid(prevChildren, childrenKept, childUidKey, ensureChildUid).map((r) =>
      r.mod === 'D' ? r : mapChildToApi ? mapChildToApi(r) : r
    ),
    ...prevChildren
      .filter((c) => deletedParentUids.has(c[parentUidKey]))
      .map((c) => ({ ...c, mod: 'D' })),
  ];

  const delC = dirtyC.filter((r) => r.mod === 'D');
  const delP = dirtyP.filter((r) => r.mod === 'D');
  const upsP = dirtyP.filter((r) => r.mod === 'A' || r.mod === 'U');
  const upsC = dirtyC.filter((r) => r.mod === 'A' || r.mod === 'U');

  if (delC.length) await commonSaveList(childSaveQuery, delC, params);
  if (delP.length) await commonSaveList(parentSaveQuery, delP, params);
  if (upsP.length) await commonSaveList(parentSaveQuery, upsP, params);
  if (upsC.length) await commonSaveList(childSaveQuery, upsC, params);

  return loadFn(dims);
}

/** 부모 1 + 자식 2종 (원인행위 vendors+contracts) */
async function saveParentTwoChildren({
  dims,
  cache,
  loadFn,
  parentUidKey,
  parentSaveQuery,
  child1UidKey,
  child1SaveQuery,
  child1ListKey,
  nextChild1,
  ensureChild1Uid,
  child2UidKey,
  child2SaveQuery,
  child2ListKey,
  nextChild2,
  ensureChild2Uid,
  nextParents,
  ensureParentUid,
  mapParentToApi,
}) {
  const key = dimsKey(dims);
  const params = dimsParams(dims);
  const prev = cacheGet(cache, key) || (await loadFn(dims));
  const prevParents = prev.masters || [];
  const prevC1 = prev[child1ListKey] || [];
  const prevC2 = prev[child2ListKey] || [];

  const dirtyP = diffRowsByUid(prevParents, nextParents, parentUidKey, ensureParentUid).map((r) =>
    r.mod === 'D' ? r : mapParentToApi ? mapParentToApi(r) : r
  );
  const deleted = new Set(dirtyP.filter((r) => r.mod === 'D').map((r) => r[parentUidKey]));

  const c1Kept = (nextChild1 || []).filter((c) => !deleted.has(c[parentUidKey]));
  const c2Kept = (nextChild2 || []).filter((c) => !deleted.has(c[parentUidKey]));
  const dirtyC1 = [
    ...diffRowsByUid(prevC1, c1Kept, child1UidKey, ensureChild1Uid),
    ...prevC1.filter((c) => deleted.has(c[parentUidKey])).map((c) => ({ ...c, mod: 'D' })),
  ];
  const dirtyC2 = [
    ...diffRowsByUid(prevC2, c2Kept, child2UidKey, ensureChild2Uid),
    ...prevC2.filter((c) => deleted.has(c[parentUidKey])).map((c) => ({ ...c, mod: 'D' })),
  ];

  const run = async (query, rows) => {
    if (rows?.length) await commonSaveList(query, rows, params);
  };
  await run(
    child1SaveQuery,
    dirtyC1.filter((r) => r.mod === 'D')
  );
  await run(
    child2SaveQuery,
    dirtyC2.filter((r) => r.mod === 'D')
  );
  await run(
    parentSaveQuery,
    dirtyP.filter((r) => r.mod === 'D')
  );
  await run(
    parentSaveQuery,
    dirtyP.filter((r) => r.mod === 'A' || r.mod === 'U')
  );
  await run(
    child1SaveQuery,
    dirtyC1.filter((r) => r.mod === 'A' || r.mod === 'U')
  );
  await run(
    child2SaveQuery,
    dirtyC2.filter((r) => r.mod === 'A' || r.mod === 'U')
  );

  return loadFn(dims);
}

const proposalCache = new Map();
const obligationCache = new Map();
const resolutionCache = new Map();
const paymentCache = new Map();
const contractReqCache = new Map();

export const SAVE_Q = {
  proposal: [
    'BdExpense.insertProposal',
    'BdExpense.updateProposal',
    'BdExpense.deleteProposal',
  ],
  proposalVendor: [
    'BdExpense.insertProposalVendor',
    'BdExpense.updateProposalVendor',
    'BdExpense.deleteProposalVendor',
  ],
  obligation: [
    'BdExpense.insertObligation',
    'BdExpense.updateObligation',
    'BdExpense.deleteObligation',
  ],
  obligationVendor: [
    'BdExpense.insertObligationVendor',
    'BdExpense.updateObligationVendor',
    'BdExpense.deleteObligationVendor',
  ],
  obligationContract: [
    'BdExpense.insertObligationContract',
    'BdExpense.updateObligationContract',
    'BdExpense.deleteObligationContract',
  ],
  resolution: [
    'BdExpense.insertResolution',
    'BdExpense.updateResolution',
    'BdExpense.deleteResolution',
  ],
  resolutionDetail: [
    'BdExpense.insertResolutionDetail',
    'BdExpense.updateResolutionDetail',
    'BdExpense.deleteResolutionDetail',
  ],
  payment: [
    'BdExpense.insertPayment',
    'BdExpense.updatePayment',
    'BdExpense.deletePayment',
  ],
  contractRequest: [
    'BdExpense.insertContractRequest',
    'BdExpense.updateContractRequest',
    'BdExpense.deleteContractRequest',
  ],
  contractRequestDoc: [
    'BdExpense.insertContractRequestDoc',
    'BdExpense.updateContractRequestDoc',
    'BdExpense.deleteContractRequestDoc',
  ],
};

function mapResolutionFromApi(row) {
  const stamp = row.processStampJson;
  const lines = row.subjectLinesJson;
  return {
    ...row,
    processStampJson:
      typeof stamp === 'string' || stamp == null ? parseJsonObject(stamp) : stamp || {},
    subjectLines: Array.isArray(lines)
      ? lines
      : parseJsonArray(typeof lines === 'string' ? lines : JSON.stringify(lines || [])),
    mod: '',
  };
}

function mapResolutionToApi(row) {
  const { subjectLines, processStampJson, ...rest } = row;
  return {
    ...rest,
    processStampJson: JSON.stringify(
      processStampJson && typeof processStampJson === 'object' ? processStampJson : {}
    ),
    subjectLinesJson: JSON.stringify(Array.isArray(subjectLines) ? subjectLines : []),
  };
}

export { isBdApiEnabled, cacheGet, cacheSet, clearMod };

export async function apiLoadProposalBundle(dims) {
  const params = dimsParams(dims);
  const [masters, vendors] = await Promise.all([
    commonSelect('BdExpense.selectProposalList', params),
    commonSelect('BdExpense.selectProposalVendorList', params),
  ]);
  const bundle = { masters: clearMod(masters), vendors: clearMod(vendors) };
  cacheSet(proposalCache, dimsKey(dims), bundle);
  return bundle;
}

export async function apiSaveProposalBundle(dims, masters, vendors, createProposalUid, createDetailUid) {
  return saveParentChildren({
    dims,
    cache: proposalCache,
    loadFn: apiLoadProposalBundle,
    parentUidKey: 'proposalUid',
    childUidKey: 'detailUid',
    parentSaveQuery: SAVE_Q.proposal,
    childSaveQuery: SAVE_Q.proposalVendor,
    nextParents: (masters || []).filter((m) => m && m.mod !== 'D'),
    nextChildren: (vendors || []).filter((v) => v && v.mod !== 'D'),
    ensureParentUid: () => createProposalUid(),
    ensureChildUid: () => createDetailUid(),
  });
}

export function getCachedProposalBundle(dims) {
  return cacheGet(proposalCache, dimsKey(dims));
}

export function setCachedProposalBundle(dims, bundle) {
  cacheSet(proposalCache, dimsKey(dims), bundle);
}

export async function apiLoadObligationBundle(dims) {
  const params = dimsParams(dims);
  const [masters, vendors, contracts] = await Promise.all([
    commonSelect('BdExpense.selectObligationList', params),
    commonSelect('BdExpense.selectObligationVendorList', params),
    commonSelect('BdExpense.selectObligationContractList', params),
  ]);
  const bundle = {
    masters: clearMod(masters),
    vendors: clearMod(vendors),
    contracts: clearMod(contracts),
  };
  cacheSet(obligationCache, dimsKey(dims), bundle);
  return bundle;
}

export async function apiSaveObligationBundle(
  dims,
  masters,
  vendors,
  contracts,
  createObligationUid,
  createDetailUid,
  createContractUid
) {
  return saveParentTwoChildren({
    dims,
    cache: obligationCache,
    loadFn: apiLoadObligationBundle,
    parentUidKey: 'obligationUid',
    parentSaveQuery: SAVE_Q.obligation,
    child1UidKey: 'detailUid',
    child1SaveQuery: SAVE_Q.obligationVendor,
    child1ListKey: 'vendors',
    nextChild1: (vendors || []).filter((v) => v && v.mod !== 'D'),
    ensureChild1Uid: () => createDetailUid(),
    child2UidKey: 'contractUid',
    child2SaveQuery: SAVE_Q.obligationContract,
    child2ListKey: 'contracts',
    nextChild2: (contracts || []).filter((c) => c && c.mod !== 'D'),
    ensureChild2Uid: () => createContractUid(),
    nextParents: (masters || []).filter((m) => m && m.mod !== 'D'),
    ensureParentUid: () => createObligationUid(),
  });
}

export function getCachedObligationBundle(dims) {
  return cacheGet(obligationCache, dimsKey(dims));
}

export function setCachedObligationBundle(dims, bundle) {
  cacheSet(obligationCache, dimsKey(dims), bundle);
}

export async function apiLoadResolutionBundle(dims) {
  const params = dimsParams(dims);
  const [mastersRaw, details] = await Promise.all([
    commonSelect('BdExpense.selectResolutionList', params),
    commonSelect('BdExpense.selectResolutionDetailList', params),
  ]);
  const bundle = {
    masters: (mastersRaw || []).map(mapResolutionFromApi),
    details: clearMod(details),
  };
  cacheSet(resolutionCache, dimsKey(dims), bundle);
  return bundle;
}

export async function apiSaveResolutionBundle(
  dims,
  masters,
  details,
  createResolutionUid,
  createDetailUid
) {
  return saveParentChildren({
    dims,
    cache: resolutionCache,
    loadFn: apiLoadResolutionBundle,
    parentUidKey: 'resolutionUid',
    childUidKey: 'detailUid',
    parentSaveQuery: SAVE_Q.resolution,
    childSaveQuery: SAVE_Q.resolutionDetail,
    nextParents: (masters || []).filter((m) => m && m.mod !== 'D'),
    nextChildren: (details || []).filter((d) => d && d.mod !== 'D'),
    ensureParentUid: () => createResolutionUid(),
    ensureChildUid: () => createDetailUid(),
    mapParentToApi: mapResolutionToApi,
  });
}

export function getCachedResolutionBundle(dims) {
  return cacheGet(resolutionCache, dimsKey(dims));
}

export function setCachedResolutionBundle(dims, bundle) {
  cacheSet(resolutionCache, dimsKey(dims), bundle);
}

export async function apiLoadPayments(dims) {
  const params = dimsParams(dims);
  const rows = clearMod(await commonSelect('BdExpense.selectPaymentList', params));
  cacheSet(paymentCache, dimsKey(dims), rows);
  return rows;
}

export async function apiSavePayments(dims, payments, createPaymentUid) {
  const key = dimsKey(dims);
  const params = dimsParams(dims);
  const prev = cacheGet(paymentCache, key) || (await apiLoadPayments(dims));
  const next = (payments || []).filter((p) => p && p.mod !== 'D');
  const dirty = diffRowsByUid(prev, next, 'paymentUid', () => createPaymentUid());
  await commonSaveList(SAVE_Q.payment, dirty, params);
  return apiLoadPayments(dims);
}

export function getCachedPayments(dims) {
  return cacheGet(paymentCache, dimsKey(dims)) || [];
}

export function setCachedPayments(dims, rows) {
  cacheSet(paymentCache, dimsKey(dims), rows);
}

export async function apiLoadContractRequestBundle(dims) {
  const params = dimsParams(dims);
  const [masters, docs] = await Promise.all([
    commonSelect('BdExpense.selectContractRequestList', params),
    commonSelect('BdExpense.selectContractRequestDocList', params),
  ]);
  const bundle = { masters: clearMod(masters), docs: clearMod(docs) };
  cacheSet(contractReqCache, dimsKey(dims), bundle);
  return bundle;
}

export async function apiSaveContractRequestBundle(
  dims,
  masters,
  docs,
  createRequestUid,
  createDocUid
) {
  return saveParentChildren({
    dims,
    cache: contractReqCache,
    loadFn: apiLoadContractRequestBundle,
    parentUidKey: 'requestUid',
    childUidKey: 'docUid',
    parentSaveQuery: SAVE_Q.contractRequest,
    childSaveQuery: SAVE_Q.contractRequestDoc,
    nextParents: (masters || []).filter((m) => m && m.mod !== 'D'),
    nextChildren: (docs || []).filter((d) => d && d.mod !== 'D'),
    ensureParentUid: () => createRequestUid(),
    ensureChildUid: () => createDocUid(),
  });
}

export function getCachedContractRequestBundle(dims) {
  return cacheGet(contractReqCache, dimsKey(dims));
}

export function setCachedContractRequestBundle(dims, bundle) {
  cacheSet(contractReqCache, dimsKey(dims), bundle);
}
